import {
  offersCoaching,
  resolveCoachAvailability,
  resolveCoachProtocols,
} from "@src/packages/injection-coach/availability";
import { useUserCountry } from "@src/packages/location/useUserCountry";
import { useCallback, useMemo, useRef, useState } from "react";
import { getTodayString } from "@src/packages/utils/date";
import type { ComponentRef } from "react";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";
import type { LayoutChangeEvent } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  Pill,
  ClipboardList,
  ShieldAlert,
  Globe,
  BookOpen,
  ExternalLink,
  Pencil,
  RefreshCw,
  FileText,
  History,
  Hash,
  AlertCircle,
  AlertTriangle,
  Syringe,
  PlayCircle,
  CircleStop,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import type {
  ContentBlock,
  ContentBlockId,
  MedicationBody,
  MedicationMarketContext,
} from "@src/packages/content";
import {
  API_FORM_TO_CONTENT_FORM,
  getBlock,
  InteractionsList,
  resolveMedicationResources,
} from "@src/packages/content";
import {
  ActionBanner,
  CardTitleText,
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  IconButton,
  CardSurface,
  PrimaryButton,
  Row,
  SecondaryButton,
  SupportingSubtitleText,
  TextActionButton,
  useScrollHaptics,
  springs,
  SectionTitle,
  TextField,
} from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../onboarding/theme";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { isProviderOwnedMedication } from "@src/packages/care/medication-ownership";
import { useMedicationLogs } from "@src/packages/care/hooks/useMedicationLogs";
import type { MedicationLog } from "@src/packages/care/hooks/useMedicationLogs";
import { useMedications } from "@src/packages/care/hooks/useMedications";
import type { Medication } from "@src/packages/care/hooks/useMedications";
import { useMedicationFormCorrection } from "@src/packages/care/useMedicationFormCorrection";
import {
  useOpenedVialEvidence,
  vialEvidenceMatters,
  useUpdateMedication,
  useDeleteMedication,
} from "@src/packages/care";
import {
  isMedicationStopped,
  parseStopReason,
  parseStoppedNoteBody,
  prependStoppedMedicationNote,
} from "@src/packages/care/medications";
import {
  MedicationRow,
  type MedicationForm,
} from "@src/packages/homepage/plan/components/MedicationRow";
import { MedicationVideoEmbed } from "./MedicationVideoEmbed";
import { InjectionCoachEntryButton } from "@src/packages/injection-coach/components/InjectionCoachEntryButton";
import { useInjectionCoach } from "@src/packages/injection-coach/components/InjectionCoachHost";
import { parseYouTubeId } from "./medicationVideo";
import { flagFromIso } from "./countryFlag";
import { SideEffectTags } from "./SideEffectTags";
import LinkedContentSheet from "./LinkedContentSheet";
import EvidenceLevelChip, { EVIDENCE_LEVELS } from "./EvidenceLevelChip";
import EvidenceLevelModal from "./EvidenceLevelModal";
import { FactSheetTag } from "./FactSheetHeader";
import { factsheetSurfaceColors } from "./medicationFactsheetTokens";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

const pillCapsuleImage =
  require("../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../assets/images/injection-pen.png") as number;
const deleteConfirmationIllustration =
  require("../../assets/images/you-icons/trash-bin.png") as number;

/**
 * Section title for the factsheet. Wraps the shared `SectionTitle` in the
 * standard `marginBottom: 10` spacer the rest of the app uses (see
 * CycleProtocolSection / ComingUpSection) so titles don't hug their content.
 */
function FactsheetSectionTitle({
  icon,
  children,
}: {
  icon?: LucideIcon;
  children: string;
}) {
  return (
    <View style={{ marginBottom: 10 }}>
      <SectionTitle icon={icon}>{children}</SectionTitle>
    </View>
  );
}

/**
 * Injection-specific tips shown inside "How to take" for injectable
 * medications. Surfaces the practical "how do I avoid bruising / pain" advice
 * patients most often ask about, route-aware (subcutaneous vs intramuscular).
 * Reuses the shared guide_* tip strings (translated en + fr).
 */
function InjectionTips({
  subcutaneous,
  intramuscular,
}: {
  subcutaneous: boolean;
  intramuscular: boolean;
}) {
  const t = useTranslations();
  if (!subcutaneous && !intramuscular) return null;

  const tips: string[] = [];
  if (subcutaneous) {
    tips.push(
      t("medication_factsheet.guide_subq_tip_1"),
      t("medication_factsheet.guide_subq_tip_2"),
      t("medication_factsheet.guide_subq_tip_3"),
      t("medication_factsheet.guide_subq_tip_4"),
    );
  }
  if (intramuscular) {
    tips.push(
      t("medication_factsheet.guide_im_tip_1"),
      t("medication_factsheet.guide_im_tip_3"),
      t("medication_factsheet.guide_im_tip_4"),
      t("medication_factsheet.injection_tip_im_help"),
    );
  }

  return (
    <View style={{ marginTop: 18 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          marginBottom: 8,
        }}
      >
        {t("medication_factsheet.injection_tips_heading")}
      </Text>
      {tips.map((tip, i) => (
        <View
          key={i}
          style={{
            flexDirection: "row",
            gap: 8,
            marginBottom: 6,
            alignItems: "flex-start",
          }}
        >
          <Text
            style={{
              fontSize: 14,
              color: EMBER.colors.warm,
              lineHeight: 22,
            }}
          >
            {"•"}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textMid,
              lineHeight: 22,
              flex: 1,
            }}
          >
            {tip}
          </Text>
        </View>
      ))}
    </View>
  );
}

function hasPastMedicationEndDate(endDate: string | null | undefined): boolean {
  if (!endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(`${endDate}T00:00:00`);
  return end.getTime() < today.getTime();
}

function getMedImage(body: MedicationBody): number {
  const forms = body.commonForms;
  if (forms.includes("pen")) return injectionPenImage;
  if (forms.includes("pre_filled_syringe")) return vialSyringeImage;
  if (forms.includes("vial")) return vialSyringeImage;
  return pillCapsuleImage;
}

const ROUTE_KEYS: Record<string, string> = {
  subcutaneous: "medication_factsheet.route_subcutaneous",
  intramuscular: "medication_factsheet.route_intramuscular",
  oral: "medication_factsheet.route_oral",
  vaginal: "medication_factsheet.route_vaginal",
  intravaginal: "medication_factsheet.route_vaginal",
  topical: "medication_factsheet.route_topical",
  nasal: "medication_factsheet.route_nasal",
  intranasal: "medication_factsheet.route_nasal",
  sublingual: "medication_factsheet.route_sublingual",
  transdermal: "medication_factsheet.route_transdermal",
  rectal: "medication_factsheet.route_rectal",
  intravenous: "medication_factsheet.route_intravenous",
};

function formatRoute(route: string, t: (key: string) => string): string {
  const key = ROUTE_KEYS[route];
  return (
    (key ? t(key) : null) ??
    route.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function needsRefrigeration(storage?: string): boolean {
  if (!storage) return false;
  const normalized = storage.toLowerCase();
  if (
    normalized.includes("do not refrigerate") ||
    normalized.includes("ne pas réfrigérer") ||
    normalized.includes("ne pas refrigerer") ||
    normalized.includes("some products may require refrigeration") ||
    normalized.includes("some compounded") ||
    normalized.includes("storage depends") ||
    normalized.includes("for best freshness")
  ) {
    return false;
  }
  return (
    normalized.includes("store in the refrigerator") ||
    normalized.includes("store unopened in the refrigerator") ||
    normalized.includes("store unopened pens in the refrigerator") ||
    normalized.includes("store unopened cartridges in the refrigerator") ||
    normalized.includes("keep refrigerated") ||
    normalized.includes("must be refrigerated") ||
    normalized.includes("stored in the refrigerator") ||
    normalized.includes("keep in the fridge") ||
    normalized.includes("store in the fridge") ||
    normalized.includes("store in a fridge") ||
    normalized.includes("conserver au réfrigérateur") ||
    normalized.includes("à conserver au réfrigérateur") ||
    normalized.includes("au réfrigérateur (2-8") ||
    normalized.includes("au refrigerateur (2-8") ||
    normalized.includes("refrigerated at 2-8") ||
    normalized.includes("refrigerated. hcg")
  );
}

const DELIVERY_METHOD_KEYS: Record<string, string> = {
  pre_filled_syringe: "medication_factsheet.delivery_pre_filled_syringe",
  pre_filled_pen: "medication_factsheet.delivery_pre_filled_pen",
  injection_solution: "medication_factsheet.delivery_injection_solution",
  lyophilized_powder: "medication_factsheet.delivery_lyophilized_powder",
  multi_vial: "medication_factsheet.delivery_multi_vial",
};

/** User-specific context that can be passed from cycle detail */
export type MedicationUserContext = {
  /** The brand name the user originally added (e.g., "Ovaleap" instead of content block title "Gonal-F") */
  originalName?: string;
  dosage?: string;
  route?: string | null;
  /** Selected delivery form (e.g., "pre_filled_pen", "injection_solution") for form-specific instructions */
  selectedForm?: string;
  /** Scheduled administration times (e.g., ["08:00", "18:00"]) */
  timeTaken?: string[];
  /** Medication start date (YYYY-MM-DD) */
  startDate?: string;
  /** Medication end date (YYYY-MM-DD) or null for ongoing */
  endDate?: string | null;
  /** User notes */
  notes?: string;
  /**
   * Record ownership from the API (`source`). Provider/system-owned records
   * render read-only: edit/stop stay hidden with an explanation (#2222).
   */
  source?: string | null;
  sinceDay?: string;
  history?: {
    cycle: string;
    dosage: string;
    outcome?: string;
    note?: string;
  }[];
  prescription?: {
    prescriber: string;
    pharmacy?: string;
    rxNumber?: string;
    refillsRemaining?: number;
    prescribedDate?: string;
    quantity?: string;
  };
};

type Props = {
  item: ContentBlock<"medication"> | null;
  onClose: () => void;
  userContext?: MedicationUserContext;
  /** Patient medication ID — enables inline Stop / Delete actions */
  medicationId?: string;
  /** Opens the shared taken/late/missed dose logger for this occurrence. */
  onLogDosePress?: () => void;
  /** Called when the user taps "Edit". When provided, opens the external add-medication modal (pre-filled) rather than an inline dose form. */
  onEditPress?: () => void;
  /** Display name when no content-db factsheet exists. Lets the sheet still
   * open with the patient context for off-catalog medications. */
  fallbackName?: string;
  /** Optional care-country override; language is never used as market data. */
  marketContext?: MedicationMarketContext;
};

type MedicationInstructionStep = {
  title: string;
  body?: string;
  warning: boolean;
};

function parseMedicationInstructionText(
  text: string,
): MedicationInstructionStep[] {
  const rawSteps = text
    .split(" | ")
    .map((s) => s.trim())
    .filter(Boolean);

  return rawSteps.map((step) => {
    const warning = step.startsWith("⚠");
    const cleaned = step
      .replace(/^⚠\s*/, "")
      .replace(/^Step\s+\d+:\s*/i, "")
      .trim();
    const match = cleaned.match(/^(.+?)\s+-\s+(.+)$/);

    if (match?.[1] && match[2]) {
      return { title: match[1].trim(), body: match[2].trim(), warning };
    }

    return { title: cleaned, warning };
  });
}

function MedicationInstructionRows({
  steps,
}: {
  steps: MedicationInstructionStep[];
}) {
  if (steps.length === 0) return null;

  const onlyStep = steps[0];

  if (steps.length === 1 && onlyStep && !onlyStep.body) {
    return (
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {onlyStep.title}
      </Text>
    );
  }

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: factsheetSurfaceColors.instructionBorder,
        backgroundColor: factsheetSurfaceColors.instructionBackground,
        overflow: "hidden",
      }}
    >
      {steps.map((step, i) => (
        <View
          key={`${step.title}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: factsheetSurfaceColors.instructionDivider,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
              marginTop: 1,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              {i + 1}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.textMid,
                lineHeight: 18,
                marginBottom: step.body ? 3 : 0,
              }}
            >
              {step.title}
            </Text>
            {step.body ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {step.body}
              </Text>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
}

function MedicationGoodToKnowRows({
  steps,
}: {
  steps: MedicationInstructionStep[];
}) {
  if (steps.length === 0) return null;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: factsheetSurfaceColors.instructionBorder,
        backgroundColor: factsheetSurfaceColors.instructionBackground,
        overflow: "hidden",
      }}
    >
      {steps.map((step, i) => (
        <View
          key={`${step.title}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: factsheetSurfaceColors.instructionDivider,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: semanticColors.status.dangerTint,
              marginTop: 1,
            }}
          >
            <AlertCircle size={13} color={EMBER.colors.coral} strokeWidth={2} />
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.coral,
                lineHeight: 18,
                marginBottom: step.body ? 3 : 0,
              }}
            >
              {step.title}
            </Text>
            {step.body ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {step.body}
              </Text>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
}

function HowToTakeSteps({ text }: { text: string }) {
  const steps = parseMedicationInstructionText(text).filter(
    (step) => !step.warning,
  );

  return <MedicationInstructionRows steps={steps} />;
}

function GoodToKnowSteps({ text }: { text: string }) {
  const steps = parseMedicationInstructionText(text).filter(
    (step) => step.warning,
  );

  return <MedicationGoodToKnowRows steps={steps} />;
}

/**
 * Safely call user-context hooks.
 * If the hooks throw (e.g. no auth context), we return empty data.
 */
function useUserMedicationContext(itemTitle: string) {
  let logsData: MedicationLog[] | undefined;
  let medsData: Medication[] | undefined;

  try {
    const logsQuery = useMedicationLogs();
    logsData = logsQuery.data;
  } catch {
    // No auth context — skip
  }

  try {
    const medsQuery = useMedications();
    medsData = medsQuery.data;
  } catch {
    // No auth context — skip
  }

  const logs = useMemo(() => logsData ?? [], [logsData]);
  const medications = useMemo(() => medsData ?? [], [medsData]);

  const matchingLogs = useMemo(() => {
    if (logs.length === 0) return [];
    const lowerTitle = itemTitle.toLowerCase();
    return logs.filter(
      (log) => log.medicationName.toLowerCase().indexOf(lowerTitle) !== -1,
    );
  }, [logs, itemTitle]);

  const currentPrescription = useMemo(() => {
    if (medications.length === 0) return null;
    const lowerTitle = itemTitle.toLowerCase();
    return (
      medications.find(
        (m) =>
          m.name?.toLowerCase() === lowerTitle ||
          m.molecule?.toLowerCase() === lowerTitle,
      ) ?? null
    );
  }, [medications, itemTitle]);

  return { matchingLogs, currentPrescription, medications };
}

function normalizeLinkedName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function blockNamesForMatching(block: ContentBlock): string[] {
  const names = [block.title];
  if (block.kind === "medication") {
    const medicationBlock = block as ContentBlock<"medication">;
    names.push(
      medicationBlock.body.genericName,
      ...(medicationBlock.body.aliases ?? []),
    );
  }
  if (block.kind === "supplement") {
    const supplementBlock = block as ContentBlock<"supplement">;
    names.push(supplementBlock.body.name);
  }
  return names.map(normalizeLinkedName).filter(Boolean);
}

function isBlockInMedicationList(
  block: ContentBlock,
  medications: Medication[],
) {
  const blockNames = blockNamesForMatching(block);
  return medications.some((medication) => {
    const medicationNames = [
      medication.name,
      medication.molecule,
      medication.moleculeType,
    ]
      .filter((value): value is string => Boolean(value))
      .map(normalizeLinkedName);
    return medicationNames.some((medicationName) =>
      blockNames.some(
        (blockName) =>
          medicationName === blockName ||
          medicationName.includes(blockName) ||
          blockName.includes(medicationName),
      ),
    );
  });
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, color: EMBER.colors.textLight }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.text,
          flexShrink: 1,
          textAlign: "right",
          marginLeft: 16,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

/** Format time array into human-readable schedule */
function formatSchedule(
  times: string[],
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  if (times.length === 0) return t("medication_factsheet.schedule_as_needed");
  const formatTime = (raw: string): string => {
    const hour = parseInt(raw.split(":")[0] ?? "0", 10);
    if (hour < 12) return t("medication_factsheet.schedule_morning");
    if (hour < 17) return t("medication_factsheet.schedule_afternoon");
    if (hour < 21) return t("medication_factsheet.schedule_evening");
    return t("medication_factsheet.schedule_night");
  };
  const labels = times.map((raw) => `${formatTime(raw)} (${raw})`);
  return t("medication_factsheet.schedule_daily_template", {
    params: {
      count: String(times.length),
      labels: labels.join(" & "),
    },
  });
}

/** Format date string for display */
function formatDateCompact(dateStr: string, locale: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeContextName(name: string | null | undefined): string {
  return (name ?? "")
    .trim()
    .toLowerCase()
    .replace(/s+/g, " ")
    .replace(/[®™]/g, "");
}

function getDistinctContextName(
  savedName: string | null | undefined,
  displayName: string,
): string | null {
  const trimmed = savedName?.trim();
  if (!trimmed) return null;
  return normalizeContextName(trimmed) === normalizeContextName(displayName)
    ? null
    : trimmed;
}

function inferMedicationRowForm({
  selectedForm,
  route,
  commonForms,
}: {
  selectedForm: string | null | undefined;
  route: string | null | undefined;
  commonForms: MedicationBody["commonForms"];
}): MedicationForm | undefined {
  const normalized = [selectedForm, route, ...commonForms]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (normalized.includes("pen")) return "pen";
  if (
    normalized.includes("vial") ||
    normalized.includes("syringe") ||
    normalized.includes("injection_solution")
  ) {
    return "vial";
  }
  if (normalized.includes("suppository") || normalized.includes("vaginal")) {
    return "suppository";
  }
  if (
    normalized.includes("oral") ||
    normalized.includes("pill") ||
    normalized.includes("tablet") ||
    normalized.includes("capsule")
  ) {
    return "pill";
  }
  return undefined;
}

/** Compute duration label from start/end dates */
function formatDuration(
  startDate: string,
  endDate: string | null | undefined,
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  if (!endDate) return t("medication_factsheet.duration_ongoing");
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  const days = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days <= 1) return t("medication_factsheet.duration_1_day");
  if (days < 7) {
    return t("medication_factsheet.duration_n_days", {
      params: { n: String(days) },
    });
  }
  if (days === 7) return t("medication_factsheet.duration_1_week");
  if (days < 30) {
    return t("medication_factsheet.duration_n_weeks", {
      params: { n: String(Math.round(days / 7)) },
    });
  }
  if (days < 60) return t("medication_factsheet.duration_1_month");
  return t("medication_factsheet.duration_n_months", {
    params: { n: String(Math.round(days / 30)) },
  });
}

function UserMedCard({
  displayName,
  commonForms,
  userContext,
  currentPrescription,
  matchingLogs,
  onEditPress,
}: {
  displayName: string;
  commonForms: MedicationBody["commonForms"];
  userContext: MedicationUserContext | undefined;
  currentPrescription: Medication | null;
  matchingLogs: MedicationLog[];
  onEditPress?: () => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  // Build dosage string from best available source
  const dosage =
    userContext?.dosage ??
    (currentPrescription?.dose != null
      ? `${currentPrescription.dose}${currentPrescription.doseUnit ? ` ${currentPrescription.doseUnit}` : ""}`
      : null) ??
    currentPrescription?.notes ??
    null;

  const { format: fmtDate } = useDateFormat();
  const hasPatientRoute =
    userContext != null &&
    Object.prototype.hasOwnProperty.call(userContext, "route");
  const rawRoute = hasPatientRoute
    ? (userContext.route ?? null)
    : (currentPrescription?.defaultRoute ??
      currentPrescription?.intakeType ??
      null);
  const route = rawRoute ? formatRoute(rawRoute, t) : null;

  const deliveryMethodKey = userContext?.selectedForm
    ? DELIVERY_METHOD_KEYS[userContext.selectedForm]
    : null;
  const deliveryMethod = deliveryMethodKey ? t(deliveryMethodKey) : null;

  const schedule =
    userContext?.timeTaken && userContext.timeTaken.length > 0
      ? formatSchedule(userContext.timeTaken, t)
      : null;

  const startDate = userContext?.startDate
    ? formatDateCompact(userContext.startDate, locale)
    : null;

  const endDate = userContext?.endDate
    ? formatDateCompact(userContext.endDate, locale)
    : null;

  const duration = userContext?.startDate
    ? formatDuration(userContext.startDate, userContext.endDate, t)
    : null;

  const since =
    userContext?.sinceDay ??
    (currentPrescription?.createdAt
      ? fmtDate(currentPrescription.createdAt)
      : null);

  const notes = userContext?.notes ?? null;
  const isStoppedContext = isMedicationStopped(notes);
  const stoppedReason = isStoppedContext ? parseStopReason(notes) : null;
  const isPastContext = hasPastMedicationEndDate(userContext?.endDate);
  const statusLabel = isStoppedContext
    ? stoppedReason
      ? `${t("medication_factsheet.context_status_stopped")} • ${stoppedReason}`
      : t("medication_factsheet.context_status_stopped")
    : isPastContext
      ? t("medication_factsheet.context_status_previous")
      : t("medication_factsheet.context_status_current");
  const patientMedicationName = getDistinctContextName(
    userContext?.originalName ?? currentPrescription?.name,
    displayName,
  );
  const rowName =
    userContext?.originalName?.trim() ||
    currentPrescription?.name?.trim() ||
    displayName;
  const dateDetail =
    isStoppedContext && endDate
      ? t("medication_factsheet.context_stopped_on", {
          params: { date: endDate },
        })
      : isPastContext && endDate
        ? t("medication_factsheet.context_ended_on", {
            params: { date: endDate },
          })
        : startDate
          ? t("medication_factsheet.context_since", {
              params: { date: startDate },
            })
          : since
            ? t("medication_factsheet.context_since", {
                params: { date: since },
              })
            : null;
  const plannedEnd =
    endDate && !isStoppedContext && !isPastContext
      ? t("medication_factsheet.context_ends_on", {
          params: { date: endDate },
        })
      : null;
  const loggedDoseSummary =
    matchingLogs.length > 0
      ? `${t("medication_factsheet.row_doses_logged")}: ${matchingLogs.length}${
          matchingLogs[0] ? ` \u2022 ${fmtDate(matchingLogs[0].takenAt)}` : ""
        }`
      : null;
  const subtitle = [
    isStoppedContext ? null : statusLabel,
    isStoppedContext ? null : dateDetail,
    schedule,
    plannedEnd,
    duration,
    loggedDoseSummary,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" \u00b7 ");
  const format = deliveryMethod ?? route ?? undefined;
  const form = inferMedicationRowForm({
    selectedForm: userContext?.selectedForm,
    route: rawRoute,
    commonForms,
  });
  const displayableNotes = parseStoppedNoteBody(notes);
  return (
    <View testID="patient-medication-summary">
      <MedicationRow
        name={patientMedicationName ?? rowName}
        dose={dosage ?? undefined}
        format={format}
        subtitle={subtitle}
        form={form}
        surface="standalone"
        rightAction={onEditPress ? "chevron" : "none"}
        onPress={onEditPress}
      />
      {displayableNotes ? (
        <View
          testID="patient-medication-notes"
          style={{
            marginTop: 10,
            padding: 12,
            borderRadius: 12,
            backgroundColor: semanticColors.surface.default,
            borderWidth: 1,
            borderColor: semanticColors.border.strong,
            gap: 4,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <FileText
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={2}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "800",
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {t("medication_factsheet.row_notes")}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.text,
            }}
          >
            {displayableNotes}
          </Text>
        </View>
      ) : null}
      {isStoppedContext ? (
        <ActionBanner
          testID="patient-medication-stopped-banner"
          variant="warning"
          title={t("medication_factsheet.stopped_banner_title")}
          message={[
            endDate
              ? t("medication_factsheet.stopped_banner_since", {
                  params: { date: endDate },
                })
              : null,
            stoppedReason
              ? t("medication_factsheet.stopped_banner_reason", {
                  params: { reason: stoppedReason },
                })
              : null,
          ]
            .filter(Boolean)
            .join("\n")}
          style={{ marginTop: 10 }}
        />
      ) : null}
    </View>
  );
}

function MedicationActionRow({
  icon: Icon,
  title,
  description,
  onPress,
  action,
  disabled,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  onPress?: () => void;
  /** Stable analytics identifier, required because `title` is translated. */
  action?: string;
  disabled?: boolean;
}) {
  return (
    <Row
      surface="glass"
      leading={
        <Icon size={16} color={EMBER.colors.textMid} strokeWidth={1.6} />
      }
      title={title}
      subtitle={description}
      onPress={onPress}
      action={action}
      disabled={disabled}
      chevron={!!onPress}
    />
  );
}

type MedicationFactsheetSectionId =
  | "purpose"
  | "howToTake"
  | "videos"
  | "goodToKnow"
  | "sideEffects"
  | "interactions"
  | "storage"
  | "availableAs"
  | "resources"
  | "evidence"
  | "history"
  | "prescription";

type MedicationFactsheetSectionNavItem = {
  id: MedicationFactsheetSectionId;
  label: string;
  icon: LucideIcon;
};

function MedicationFactsheetSectionChips({
  items,
  onPress,
}: {
  items: MedicationFactsheetSectionNavItem[];
  onPress: (id: MedicationFactsheetSectionId) => void;
}) {
  if (items.length < 2) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        gap: 8,
        paddingVertical: 14,
        paddingHorizontal: 2,
      }}
      style={{ marginTop: 4 }}
    >
      {items.map((item) => (
        <CompactActionButton
          key={item.id}
          label={item.label}
          action={`medication_section_${item.id}`}
          variant="secondary"
          size="sm"
          leadingIcon={item.icon}
          onPress={() => onPress(item.id)}
        />
      ))}
    </ScrollView>
  );
}

export function MedicationDetailContent({
  item,
  onClose,
  userContext,
  skipExitAnimation,
  medicationId,
  onLogDosePress,
  onEditPress,
  marketContext,
}: {
  item: ContentBlock<"medication">;
  onClose: () => void;
  userContext?: MedicationUserContext;
  skipExitAnimation?: boolean;
  medicationId?: string;
  onLogDosePress?: () => void;
  onEditPress?: () => void;
  marketContext?: MedicationMarketContext;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationDetailContent");
  const { countryCode: userCountry } = useUserCountry();
  const insets = useSafeAreaInsets();
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const onScrollHaptics = useScrollHaptics();
  // "Available as" is collapsed by default to keep the factsheet uncluttered.
  const [brandsExpanded, setBrandsExpanded] = useState(false);
  const scrollRef = useRef<ComponentRef<typeof Animated.ScrollView> | null>(
    null,
  );
  const contentCardOffsetYRef = useRef(0);
  const sectionOffsetsRef = useRef<
    Partial<Record<MedicationFactsheetSectionId, number>>
  >({});

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  const handleContentCardLayout = useCallback((e: LayoutChangeEvent) => {
    contentCardOffsetYRef.current = e.nativeEvent.layout.y;
  }, []);

  const registerSectionLayout = useCallback(
    (sectionId: MedicationFactsheetSectionId) => (e: LayoutChangeEvent) => {
      sectionOffsetsRef.current[sectionId] =
        contentCardOffsetYRef.current + e.nativeEvent.layout.y;
    },
    [],
  );

  const handleSectionNavPress = useCallback(
    (sectionId: MedicationFactsheetSectionId) => {
      const y = sectionOffsetsRef.current[sectionId];
      if (typeof y !== "number") return;
      scrollRef.current?.scrollTo({
        y: Math.max(y - 12, 0),
        animated: true,
      });
    },
    [],
  );

  const body = item.body as MedicationBody;
  const { locale } = useLocale();
  const resolvedMarketContext = useMemo(
    () => ({
      ...marketContext,
      homeCountry: marketContext?.homeCountry ?? userCountry,
      fallbackCountry: marketContext?.fallbackCountry ?? userCountry,
    }),
    [marketContext, userCountry],
  );
  const fr = locale === "fr" ? body.frBody : undefined;
  const requiresRefrigeration = needsRefrigeration(
    `${body.storage ?? ""} ${body.frBody?.storage ?? ""}`,
  );
  const selectedForm = userContext?.selectedForm;
  const contentFormKey = selectedForm
    ? API_FORM_TO_CONTENT_FORM[selectedForm]
    : undefined;
  const localizedByForm = fr?.howToTakeByForm ?? body.howToTakeByForm;
  const formSpecific =
    contentFormKey && localizedByForm
      ? localizedByForm[contentFormKey as keyof typeof localizedByForm]
      : undefined;

  /**
   * Whether the guided walk covers her, which is what lets this page stop
   * numbering gestures.
   *
   * The same predicate the entry button renders itself on, deliberately: two
   * conditions that could disagree would give her an overview and no guide.
   * A medicine sold as more than one vial is the case that forced this — the
   * procedures differ and nothing stored says which is hers, so one page of
   * copy has to either guess, caveat itself, or step back and let the walk ask
   * (Anaïs, 2026-09-30).
   *
   * Where the walk does not reach her — an unsupported market, or a country the
   * browser does not report — the step-by-step below is the only procedure in
   * the app, and she keeps it.
   */
  const guideCoversHer =
    Boolean(useInjectionCoach()) &&
    offersCoaching(
      resolveCoachAvailability({
        routes: body.commonRoutes,
        medicationName: userContext?.originalName ?? item.title,
        // US stands in until the profile has a country, as the region gate does.
        countryCode: userCountry ?? "US",
      }),
    );
  const overview = guideCoversHer
    ? (fr?.howToTakeOverview ?? body.howToTakeOverview)
    : undefined;
  const howToTakeText =
    overview ?? formSpecific ?? fr?.howToTake ?? body.howToTake;
  const goodToKnowSteps = parseMedicationInstructionText(howToTakeText).filter(
    (step) => step.warning,
  );

  // Manufacturer how-to videos: YouTube links embed inline; everything else
  // (Vimeo, manufacturer sites, guides, info) stays a tap-out link row.
  const videoEmbeds = (body.resources ?? []).flatMap((res) => {
    const videoId = res.type === "video" ? parseYouTubeId(res.url) : null;
    return videoId ? [{ videoId, label: res.label, url: res.url }] : [];
  });
  const linkResources = (body.resources ?? []).filter(
    (res) => !(res.type === "video" && parseYouTubeId(res.url)),
  );
  const officialResources = resolveMedicationResources(
    {
      ...body,
      resources: linkResources,
    },
    item.id,
    resolvedMarketContext,
    (brand) =>
      t("medication_factsheet.official_resource_ansm_label", {
        params: { brand },
      }),
  );
  const { matchingLogs, currentPrescription, medications } =
    useUserMedicationContext(item.title);
  const [linkedItem, setLinkedItem] = useState<
    ContentBlock<"medication"> | ContentBlock<"supplement"> | null
  >(null);
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);
  const takingLinkedBlockIds = useMemo(
    () =>
      (body.interactions ?? [])
        .map((interaction) => interaction.linkedBlockId)
        .filter((id): id is string => Boolean(id))
        .filter((id) => {
          const linkedBlock = getBlock(id as ContentBlockId);
          return linkedBlock
            ? isBlockInMedicationList(linkedBlock, medications)
            : false;
        }),
    [body.interactions, medications],
  );
  const handleLinkedBlockPress = useCallback((blockId: string) => {
    const linkedBlock = getBlock(blockId as ContentBlockId);
    if (
      linkedBlock?.kind === "medication" ||
      linkedBlock?.kind === "supplement"
    ) {
      setLinkedItem(
        linkedBlock as ContentBlock<"medication"> | ContentBlock<"supplement">,
      );
    }
  }, []);

  // Merge hook-detected prescription with explicit userContext
  const prescription = userContext?.prescription ?? null;
  const history = userContext?.history ?? null;
  const sideEffects = fr?.sideEffects ?? body.sideEffects;
  const storageText = fr?.storage ?? body.storage;
  const sectionNavigationItems = useMemo(() => {
    const items: MedicationFactsheetSectionNavItem[] = [
      {
        id: "purpose",
        label: t("medication_factsheet.section_purpose"),
        icon: BookOpen,
      },
      {
        id: "howToTake",
        label: t("medication_factsheet.section_how_to_take"),
        icon: ClipboardList,
      },
    ];

    if (videoEmbeds.length) {
      items.push({
        id: "videos",
        label: t("medication_factsheet.section_videos"),
        icon: PlayCircle,
      });
    }
    if (goodToKnowSteps.length) {
      items.push({
        id: "goodToKnow",
        label: t("medication_factsheet.section_good_to_know"),
        icon: AlertCircle,
      });
    }
    if (sideEffects?.length) {
      items.push({
        id: "sideEffects",
        label: t("medication_factsheet.section_side_effects"),
        icon: ShieldAlert,
      });
    }
    if (body.interactions?.length) {
      items.push({
        id: "interactions",
        label: t("medication_factsheet.section_interacts_with"),
        icon: AlertTriangle,
      });
    }
    if (storageText) {
      items.push({
        id: "storage",
        label: t("medication_factsheet.section_storage"),
        icon: AlertCircle,
      });
    }
    if (body.availability?.length || body.brandsByCountry?.length) {
      items.push({
        id: "availableAs",
        label: t("medication_factsheet.section_available_as"),
        icon: Globe,
      });
    }
    if (officialResources.length) {
      items.push({
        id: "resources",
        label: t("medication_factsheet.section_official_resources"),
        icon: BookOpen,
      });
    }
    if (item.sources?.length) {
      items.push({
        id: "evidence",
        label: t("medication_factsheet.section_evidence"),
        icon: BookOpen,
      });
    }
    if (history?.length) {
      items.push({
        id: "history",
        label: t("medication_factsheet.section_your_history"),
        icon: History,
      });
    }
    if (prescription) {
      items.push({
        id: "prescription",
        label: t("medication_factsheet.section_prescription"),
        icon: FileText,
      });
    }

    return items;
  }, [
    body.availability?.length,
    body.brandsByCountry?.length,
    body.interactions?.length,
    goodToKnowSteps.length,
    history?.length,
    item.sources?.length,
    officialResources.length,
    prescription,
    sideEffects?.length,
    storageText,
    t,
    videoEmbeds.length,
  ]);

  // Mutations — only active when medicationId is present
  const updateMedication = useUpdateMedication();
  // The coach reads her recorded administration method instead of asking. When
  // she says it is wrong, the calendar learns it here — a wrong record is wrong
  // for this sheet too, and for the next cycle's walk.
  const correctMedicationForm = useMedicationFormCorrection(
    medicationId,
    userContext?.selectedForm,
  );
  // Whether her vial is already mixed, for the one medication where that
  // changes the procedure. Read here, passed down: the coach never fetches.
  const hasEarlierDose = useOpenedVialEvidence(medicationId, {
    // Only where the answer can change a walk. One medication qualifies today,
    // and without this the read ran once per medication row on screen.
    enabled: vialEvidenceMatters(
      resolveCoachProtocols(userContext?.originalName ?? item.title)
        ?.protocols ?? [],
    ),
  });
  const deleteMedication = useDeleteMedication();
  const isStopped = isMedicationStopped(userContext?.notes);
  const isMutating = updateMedication.isPending || deleteMedication.isPending;

  // Inline action modes: view = default, stop = reason form. Edit opens the external AddMedicationModal via onEditPress.
  type ActionMode = "view" | "stop";
  const [mode, setMode] = useState<ActionMode>("view");

  const [stopReason, setStopReason] = useState("");
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const openStop = useCallback(() => {
    setStopReason("");
    setMutationError(null);
    setMode("stop");
  }, []);

  const handleConfirmStop = useCallback(() => {
    if (!medicationId) {
      setMutationError(t("medication_factsheet.missing_med_id"));
      return;
    }
    setMutationError(null);
    const reason = stopReason.trim();
    const todayISO = getTodayString();
    updateMedication.mutate(
      {
        medicationId,
        endDate: todayISO,
        notes: prependStoppedMedicationNote(userContext?.notes, reason),
      },
      {
        onSuccess: () => {
          setMode("view");
          handleClose();
        },
        onError: (err) => {
          console.error("[MedicationFactsheet] Stop failed", err);
          setMutationError(
            err instanceof Error
              ? err.message
              : t("medication_factsheet.stop_med_error"),
          );
        },
      },
    );
  }, [
    medicationId,
    stopReason,
    userContext?.notes,
    updateMedication,
    handleClose,
    t,
  ]);

  const handleDelete = useCallback(() => {
    if (!medicationId) return;
    setMutationError(null);
    setDeleteConfirmVisible(true);
  }, [medicationId]);

  const handleConfirmDelete = useCallback(() => {
    if (!medicationId) return;
    deleteMedication.mutate(medicationId, {
      onSuccess: () => {
        setDeleteConfirmVisible(false);
        handleClose();
      },
      onError: (err) => {
        console.error("[MedicationFactsheet] Delete failed", err);
        setMutationError(
          err instanceof Error
            ? err.message
            : t("medication_factsheet.delete_med_error"),
        );
        setDeleteConfirmVisible(false);
      },
    });
  }, [medicationId, deleteMedication, handleClose, t]);

  const providerOwned = isProviderOwnedMedication(userContext?.source);

  const medicationRecordActions =
    medicationId || prescription ? (
      <View
        style={{
          marginTop: 8,
          gap: 8,
        }}
      >
        {mode === "view" && providerOwned && (
          <MedicationActionRow
            icon={ShieldAlert}
            title={t("medication_factsheet.provider_owned_title")}
            description={t("medication_factsheet.provider_owned_note")}
          />
        )}
        {mode === "view" && (
          <View style={{ gap: 8 }}>
            {medicationId && onEditPress && !providerOwned && (
              <MedicationActionRow
                icon={Pencil}
                title={t("medication_factsheet.action_edit")}
                description={t("medication_factsheet.action_edit_help")}
                onPress={onEditPress}
                action="medication_factsheet.action_edit"
                disabled={isMutating}
              />
            )}

            {medicationId && onLogDosePress && (
              <MedicationActionRow
                icon={ClipboardList}
                title={t("medication_factsheet.action_log_dose")}
                description={t("medication_factsheet.action_log_dose_help")}
                onPress={onLogDosePress}
                action="medication_factsheet.action_log_dose"
                disabled={isMutating}
              />
            )}

            {prescription && (
              <MedicationActionRow
                icon={RefreshCw}
                title={t("medication_factsheet.action_refill")}
                description={t("medication_factsheet.action_refill_help", {
                  params: {
                    pharmacy: prescription.pharmacy ?? "your pharmacy",
                  },
                })}
              />
            )}

            {medicationId && !isStopped && !providerOwned && (
              <MedicationActionRow
                icon={ShieldAlert}
                title={t("medication_factsheet.action_stop")}
                description={t("medication_factsheet.action_stop_help")}
                onPress={openStop}
                action="medication_factsheet.action_stop"
                disabled={isMutating}
              />
            )}
          </View>
        )}

        {mode === "stop" && (
          <CardSurface style={{ padding: 14, gap: 12 }}>
            <View style={{ gap: 4 }}>
              <CardTitleText>
                {t("medication_factsheet.stop_form_title")}
              </CardTitleText>
              <SupportingSubtitleText>
                {t("medication_factsheet.stop_form_help", {
                  params: {
                    name: userContext?.originalName ?? item.title,
                  },
                })}
              </SupportingSubtitleText>
            </View>
            <TextField
              value={stopReason}
              onChangeText={setStopReason}
              placeholder={t("medication_factsheet.stop_form_placeholder")}
              notes
            />
            {mutationError ? (
              <ActionBanner variant="important" message={mutationError} />
            ) : null}
            <View style={{ flexDirection: "row", gap: 8 }}>
              <SecondaryButton
                label={t("medication_factsheet.stop_form_cancel")}
                action="medication_factsheet.stop_form_cancel"
                onPress={() => setMode("view")}
                disabled={isMutating}
                flex={1}
              />
              <PrimaryButton
                label={t("medication_factsheet.stop")}
                action="medication_factsheet.stop_form_submit"
                leading={
                  <CircleStop
                    size={16}
                    color={semanticColors.action.primary.foreground}
                    strokeWidth={1.8}
                  />
                }
                onPress={handleConfirmStop}
                disabled={isMutating}
                loading={updateMedication.isPending}
                flex={1}
              />
            </View>
            {medicationId ? (
              <TextActionButton
                label={t("medication_factsheet.delete_mistake_action")}
                action="medication_factsheet.delete_mistake_action"
                tone="destructive"
                size="sm"
                onPress={handleDelete}
                disabled={isMutating}
                centered
              />
            ) : null}
          </CardSurface>
        )}
      </View>
    ) : null;

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            ref={scrollRef}
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <CardSurface
              onLayout={handleContentCardLayout}
              style={{
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor={semanticColors.surface.muted}
                  pressedBackgroundColor={semanticColors.surface.muted}
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              {/* Medication illustration */}
              <View
                style={{
                  alignItems: "center",
                  paddingTop: 8,
                  paddingBottom: 20,
                }}
              >
                <Image
                  source={getMedImage(body)}
                  style={{ width: 80, height: 80 }}
                  resizeMode="contain"
                />
              </View>

              {/* Title — centered */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  lineHeight: 30,
                  marginBottom: 4,
                  textAlign: "center",
                }}
              >
                {userContext?.originalName ?? item.title}
              </Text>
              {body.genericName ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textLight,
                    textAlign: "center",
                    marginBottom: 6,
                  }}
                >
                  {fr?.genericName ?? body.genericName}
                </Text>
              ) : null}

              {/* Tag row, centered like the other fact sheets */}
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 18,
                  marginTop: 10,
                  justifyContent: "center",
                }}
              >
                <FactSheetTag
                  tag={{
                    kind: "category",
                    label: fr?.drugClass ?? body.drugClass,
                    icon: Pill,
                  }}
                />
                {body.commonRoutes.length > 0 && (
                  <FactSheetTag
                    tag={{
                      kind: "route",
                      label: body.commonRoutes
                        .map((r) => formatRoute(r, t))
                        .join(", "),
                      icon: Syringe,
                    }}
                  />
                )}
                {requiresRefrigeration && (
                  <FactSheetTag
                    tag={{
                      kind: "refrigerated",
                      label: t("medication_factsheet.keep_refrigerated"),
                    }}
                  />
                )}
                {body.forWhom && (
                  <FactSheetTag
                    tag={{ kind: "audience", audience: body.forWhom }}
                  />
                )}
                {isStopped && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                      paddingHorizontal: 10,
                      paddingVertical: 5,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: withAlpha(
                        semanticColors.status.danger,
                        0.25,
                      ),
                      backgroundColor: semanticColors.status.dangerTint,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        fontWeight: "600",
                        color: EMBER.colors.coral,
                        letterSpacing: 0.2,
                      }}
                    >
                      {t("medication_factsheet.stopped_badge")}
                    </Text>
                  </View>
                )}
              </View>

              {/* "You're taking this" — from hooks OR userContext */}
              {userContext || currentPrescription ? (
                medicationRecordActions ? (
                  <View
                    style={{
                      borderRadius: 18,
                      borderWidth: 1,
                      borderColor: "rgba(255, 255, 255, 0.78)",
                      backgroundColor: semanticColors.surface.input,
                      padding: 14,
                    }}
                  >
                    <UserMedCard
                      displayName={userContext?.originalName ?? item.title}
                      commonForms={body.commonForms}
                      userContext={userContext}
                      currentPrescription={currentPrescription}
                      matchingLogs={matchingLogs}
                      onEditPress={onEditPress}
                    />
                    {medicationRecordActions}
                  </View>
                ) : (
                  <UserMedCard
                    displayName={userContext?.originalName ?? item.title}
                    commonForms={body.commonForms}
                    userContext={userContext}
                    currentPrescription={currentPrescription}
                    matchingLogs={matchingLogs}
                    onEditPress={onEditPress}
                  />
                )
              ) : null}

              {/* "You've taken this before" — only logs, no active prescription */}
              {!userContext &&
              !currentPrescription &&
              matchingLogs.length > 0 ? (
                <UserMedCard
                  displayName={item.title}
                  commonForms={body.commonForms}
                  userContext={undefined}
                  currentPrescription={null}
                  matchingLogs={matchingLogs}
                />
              ) : null}

              {!userContext && !currentPrescription
                ? medicationRecordActions
                : null}

              <MedicationFactsheetSectionChips
                items={sectionNavigationItems}
                onPress={handleSectionNavPress}
              />

              {/* Purpose */}
              <View
                onLayout={registerSectionLayout("purpose")}
                style={{ marginTop: 24 }}
              >
                <FactsheetSectionTitle icon={BookOpen}>
                  {t("medication_factsheet.section_purpose")}
                </FactsheetSectionTitle>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textMid,
                    lineHeight: 21,
                  }}
                >
                  {fr?.purpose ?? body.purpose}
                </Text>
              </View>

              {/* How to take */}
              <View
                onLayout={registerSectionLayout("howToTake")}
                style={{ marginTop: 24 }}
              >
                <FactsheetSectionTitle icon={ClipboardList}>
                  {t("medication_factsheet.section_how_to_take")}
                </FactsheetSectionTitle>
                <HowToTakeSteps text={howToTakeText} />
                <InjectionTips
                  subcutaneous={body.commonRoutes.includes("subcutaneous")}
                  intramuscular={body.commonRoutes.includes("intramuscular")}
                />
              </View>

              {/* How-to videos — inline manufacturer injection/mixing guides */}
              {videoEmbeds.length ? (
                <View
                  onLayout={registerSectionLayout("videos")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={PlayCircle}>
                    {t("medication_factsheet.section_videos")}
                  </FactsheetSectionTitle>
                  <View style={{ gap: 16 }}>
                    {videoEmbeds.map((video) => (
                      <MedicationVideoEmbed
                        key={video.url}
                        videoId={video.videoId}
                        label={video.label}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {/* Guided injection — sits under the manufacturer videos because it
                  is the same question answered live: the videos show how it is
                  done, this walks her through doing it. It renders itself only
                  where an approved procedure exists and an escalation is in
                  reach, so there is nothing to gate here. */}
              <View style={{ marginTop: 24 }}>
                <InjectionCoachEntryButton
                  // Her prescription's name, not the catalogue's title. The
                  // Ovidrel entry is titled "Ovidrel"; a patient in France was
                  // prescribed Ovitrelle, and naming the catalogue at the moment
                  // #2267 requires medication identity to be verified is the
                  // exact failure this feature's market axis exists to prevent.
                  // Every other name render in this component already does this.
                  medicationName={userContext?.originalName ?? item.title}
                  routes={body.commonRoutes}
                  // Only when this sheet was opened against a scheduled dose.
                  // The factsheet is also a reference page, and there is nothing
                  // to record from one of those. The comment said so before the
                  // code did — `medicationId` is the same gate the "log a dose"
                  // action itself uses, and without it the walk ended by
                  // promising a confirmation that had nothing to confirm.
                  // Same gate as the action below it: only a sheet opened
                  // against a scheduled dose has one to name.
                  patientMedicationId={medicationId}
                  // Her own recorded administration method, raw. `selectedForm`
                  // is what she picked in "méthode d'administration"; the coach
                  // translates it rather than asking her again.
                  apiForm={userContext?.selectedForm}
                  onFormCorrected={correctMedicationForm}
                  hasEarlierDose={hasEarlierDose}
                  onCoachFinished={
                    medicationId && onLogDosePress ? onLogDosePress : undefined
                  }
                  // A dose she confirms against her box, written back so the
                  // record is right next time. Same gate: only a sheet opened
                  // against a scheduled dose has a record to correct.
                  onCorrectDose={
                    medicationId
                      ? (dose) =>
                          updateMedication.mutate({
                            medicationId,
                            dose: dose.value,
                            doseUnit: dose.unit,
                          })
                      : undefined
                  }
                />
              </View>

              {goodToKnowSteps.length ? (
                <View
                  onLayout={registerSectionLayout("goodToKnow")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={AlertCircle}>
                    {t("medication_factsheet.section_good_to_know")}
                  </FactsheetSectionTitle>
                  <GoodToKnowSteps text={howToTakeText} />
                </View>
              ) : null}

              {/* Side effects \u2014 tappable pill tags; tap one you're experiencing
                  to log it for today (persists to the symptom tracker). */}
              {sideEffects?.length ? (
                <View
                  onLayout={registerSectionLayout("sideEffects")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={ShieldAlert}>
                    {t("medication_factsheet.section_side_effects")}
                  </FactsheetSectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                      marginBottom: 10,
                    }}
                  >
                    {t("medication_factsheet.side_effects_log_hint")}
                  </Text>
                  <SideEffectTags sideEffects={sideEffects} />
                </View>
              ) : null}

              {/* Interactions with other meds / supplements */}
              {body.interactions?.length ? (
                <View
                  onLayout={registerSectionLayout("interactions")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={AlertTriangle}>
                    {t("medication_factsheet.section_interacts_with")}
                  </FactsheetSectionTitle>
                  <InteractionsList
                    interactions={body.interactions}
                    onLinkedBlockPress={handleLinkedBlockPress}
                    takingLinkedBlockIds={takingLinkedBlockIds}
                    takingLabel={t("medication_factsheet.interaction_taking")}
                    severityLabels={{
                      note: t("medication_factsheet.interaction_severity_note"),
                      caution: t(
                        "medication_factsheet.interaction_severity_caution",
                      ),
                      critical: t(
                        "medication_factsheet.interaction_severity_critical",
                      ),
                    }}
                  />
                </View>
              ) : null}

              {/* Storage */}
              {storageText ? (
                <View
                  onLayout={registerSectionLayout("storage")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={AlertCircle}>
                    {t("medication_factsheet.section_storage")}
                  </FactsheetSectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.textMid,
                      lineHeight: 21,
                    }}
                  >
                    {storageText}
                  </Text>
                </View>
              ) : null}

              {/* Available as — collapsible. Newer shape (`availability`)
                  groups by brand and shows flags of the markets where it's
                  sold; falls back to `brandsByCountry`. Official
                  documentation links render in the bottom resources section. */}
              {body.availability?.length || body.brandsByCountry?.length ? (
                <View
                  onLayout={registerSectionLayout("availableAs")}
                  style={{ marginTop: 24 }}
                >
                  <Accordion
                    type="multiple"
                    value={brandsExpanded ? ["available-as"] : []}
                    onValueChange={(value: string[]) =>
                      setBrandsExpanded(value.includes("available-as"))
                    }
                  >
                    <AccordionItem value="available-as">
                      <AccordionTrigger
                        accessibilityLabel={`${
                          brandsExpanded ? "Collapse" : "Expand"
                        } ${t("medication_factsheet.section_available_as")}`}
                        analyticsLabel={t(
                          "medication_factsheet.section_available_as",
                        )}
                        action={{
                          expand: "expand_available_as",
                          collapse: "collapse_available_as",
                        }}
                        className="items-center py-0"
                      >
                        <SectionTitle icon={Globe}>
                          {t("medication_factsheet.section_available_as")}
                        </SectionTitle>
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        <View style={{ paddingTop: 10 }}>
                          {body.availability?.length
                            ? body.availability.map((brand, i) => {
                                return (
                                  <View key={i} style={{ marginBottom: 12 }}>
                                    <Text
                                      className="font-dm-sans"
                                      style={{
                                        fontSize: 14,
                                        fontWeight: "600",
                                        color: EMBER.colors.textMid,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {brand.name}
                                      {brand.manufacturer ? (
                                        <Text
                                          className="font-dm-sans"
                                          style={{
                                            fontWeight: "400",
                                            color: EMBER.colors.textLight,
                                          }}
                                        >
                                          {`  ·  ${brand.manufacturer}`}
                                        </Text>
                                      ) : null}
                                    </Text>
                                    <Text
                                      style={{ fontSize: 18, lineHeight: 26 }}
                                    >
                                      {brand.countries
                                        .map(flagFromIso)
                                        .filter(Boolean)
                                        .join(" ")}
                                    </Text>
                                  </View>
                                );
                              })
                            : body.brandsByCountry?.map((entry, i) => {
                                return (
                                  <View key={i} style={{ marginBottom: 10 }}>
                                    <Text
                                      className="font-dm-sans"
                                      style={{
                                        fontSize: 13,
                                        fontWeight: "600",
                                        color: EMBER.colors.textMid,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {entry.country}
                                    </Text>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        flexWrap: "wrap",
                                        gap: 6,
                                      }}
                                    >
                                      {entry.brands.map((brand) => (
                                        <View
                                          key={brand}
                                          style={{
                                            backgroundColor:
                                              factsheetSurfaceColors.brandTagBackground,
                                            borderRadius: 8,
                                            paddingHorizontal: 10,
                                            paddingVertical: 5,
                                          }}
                                        >
                                          <Text
                                            className="font-dm-sans"
                                            style={{
                                              fontSize: 13,
                                              color: EMBER.colors.textLight,
                                            }}
                                          >
                                            {brand}
                                          </Text>
                                        </View>
                                      ))}
                                    </View>
                                  </View>
                                );
                              })}
                        </View>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </View>
              ) : null}

              {/* Official Resources */}
              {officialResources.length ? (
                <View
                  onLayout={registerSectionLayout("resources")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={BookOpen}>
                    {t("medication_factsheet.section_official_resources")}
                  </FactsheetSectionTitle>
                  {officialResources.map((res) => (
                    <Pressable
                      key={res.url}
                      onPress={() => {
                        // Resource labels are authored catalogue copy.
                        capturePress(
                          pressLabel(res.label),
                          "official_resource_link",
                        );
                        Linking.openURL(res.url);
                      }}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                        paddingVertical: 10,
                      }}
                    >
                      <View
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          backgroundColor:
                            res.type === "video"
                              ? withAlpha(semanticColors.accent.default, 0.1)
                              : semanticColors.surface.muted,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Text style={{ fontSize: 13 }}>
                          {res.type === "video"
                            ? "\u25B6"
                            : res.type === "guide"
                              ? "\uD83D\uDCC4"
                              : "\u2139\uFE0F"}
                        </Text>
                      </View>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          fontWeight: "500",
                          color: EMBER.colors.textMid,
                          flex: 1,
                        }}
                      >
                        {res.label}
                      </Text>
                      <ExternalLink
                        size={13}
                        color={EMBER.colors.textFaint}
                        strokeWidth={1.5}
                      />
                    </Pressable>
                  ))}
                </View>
              ) : null}

              {/* Evidence sources */}
              {item.sources?.length ? (
                <View
                  onLayout={registerSectionLayout("evidence")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={BookOpen}>
                    {t("medication_factsheet.section_evidence")}
                  </FactsheetSectionTitle>
                  {item.sources.map((source, i) => (
                    <View key={i} style={{ gap: 8, marginBottom: 16 }}>
                      {source.evidenceLevel &&
                        EVIDENCE_LEVELS[source.evidenceLevel] && (
                          <EvidenceLevelChip
                            level={source.evidenceLevel}
                            onPress={() =>
                              setEvidenceModal(source.evidenceLevel!)
                            }
                          />
                        )}
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          color: EMBER.colors.textMid,
                          lineHeight: 20,
                        }}
                      >
                        {source.title}
                        {source.journal ? ` \u2014 ${source.journal}` : ""}
                        {source.year ? ` (${source.year})` : ""}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}

              {/* User context: History timeline (from userContext prop) */}
              {history?.length ? (
                <View
                  onLayout={registerSectionLayout("history")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={History}>
                    {t("medication_factsheet.section_your_history")}
                  </FactsheetSectionTitle>
                  {history.map((h, i) => {
                    const isCurrent = i === history.length - 1;
                    return (
                      <View
                        key={h.cycle}
                        style={{
                          flexDirection: "row",
                          gap: 12,
                          paddingVertical: 12,
                        }}
                      >
                        <View style={{ alignItems: "center", paddingTop: 4 }}>
                          <View
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: isCurrent
                                ? EMBER.colors.warm
                                : semanticColors.border.subtle,
                              borderWidth: isCurrent ? 0 : 1.5,
                              borderColor: semanticColors.border.strong,
                            }}
                          />
                        </View>
                        <View style={{ flex: 1 }}>
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 14,
                                fontWeight: "600",
                                color: isCurrent
                                  ? EMBER.colors.text
                                  : EMBER.colors.textMid,
                              }}
                            >
                              {h.cycle}
                              {isCurrent ? " (current)" : ""}
                            </Text>
                            <View
                              style={{
                                paddingHorizontal: 8,
                                paddingVertical: 2,
                                borderRadius: 6,
                                backgroundColor: semanticColors.surface.muted,
                              }}
                            >
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 11,
                                  fontWeight: "600",
                                  color: EMBER.colors.textLight,
                                }}
                              >
                                {h.dosage}
                              </Text>
                            </View>
                          </View>
                          {h.outcome && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 13,
                                color: semanticColors.status.success,
                                fontWeight: "500",
                                marginTop: 4,
                              }}
                            >
                              {h.outcome}
                            </Text>
                          )}
                          {h.note && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 13,
                                color: EMBER.colors.textLight,
                                lineHeight: 19,
                                marginTop: 3,
                              }}
                            >
                              {h.note}
                            </Text>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              ) : null}

              {/* User context: Prescription (from userContext prop) */}
              {prescription ? (
                <View
                  onLayout={registerSectionLayout("prescription")}
                  style={{ marginTop: 24 }}
                >
                  <FactsheetSectionTitle icon={FileText}>
                    {t("medication_factsheet.section_prescription")}
                  </FactsheetSectionTitle>
                  <View
                    style={{
                      backgroundColor: semanticColors.surface.muted,
                      borderRadius: 12,
                      padding: 14,
                      gap: 10,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 13, color: EMBER.colors.textLight }}
                      >
                        {t("medication_factsheet.row_prescriber")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          fontWeight: "600",
                          color: EMBER.colors.text,
                        }}
                      >
                        {prescription.prescriber}
                      </Text>
                    </View>
                    {prescription.pharmacy && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_pharmacy")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {prescription.pharmacy}
                        </Text>
                      </View>
                    )}
                    {prescription.rxNumber && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_rx_number")}
                        </Text>
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Hash
                            size={11}
                            color={EMBER.colors.textLight}
                            strokeWidth={1.5}
                          />
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 13,
                              fontWeight: "600",
                              color: EMBER.colors.text,
                              fontVariant: ["tabular-nums"],
                            }}
                          >
                            {prescription.rxNumber}
                          </Text>
                        </View>
                      </View>
                    )}
                    {prescription.quantity && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_quantity")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {prescription.quantity}
                        </Text>
                      </View>
                    )}
                    {prescription.prescribedDate && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_prescribed")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {prescription.prescribedDate}
                        </Text>
                      </View>
                    )}
                    {prescription.refillsRemaining !== undefined && (
                      <View
                        style={{
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t("medication_factsheet.row_refills_remaining")}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color:
                              prescription.refillsRemaining === 0
                                ? semanticColors.accent.default
                                : EMBER.colors.text,
                          }}
                        >
                          {prescription.refillsRemaining === 0
                            ? t("medication_factsheet.refills_none")
                            : prescription.refillsRemaining}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              ) : null}

              {/* Disclaimer */}
              <View
                style={{
                  marginTop: 28,
                  paddingTop: 20,
                  borderTopWidth: 1,
                  borderTopColor: factsheetSurfaceColors.disclaimerDivider,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                    lineHeight: 18,
                  }}
                >
                  {t("medication_factsheet.disclaimer")}
                </Text>
              </View>
            </CardSurface>
          </Animated.ScrollView>
        </View>
      </Animated.View>

      <LinkedContentSheet
        item={linkedItem}
        onClose={() => setLinkedItem(null)}
      />

      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />

      <Dialog
        visible={deleteConfirmVisible}
        onClose={() => {
          if (!deleteMedication.isPending) setDeleteConfirmVisible(false);
        }}
        closeButton="minimal"
        backdropPressLabel="dismiss_delete_medication_backdrop"
        testID="medication-delete-confirmation-dialog"
      >
        <DialogIcon source={deleteConfirmationIllustration} />
        <DialogHeader
          align="center"
          title={t("medication_factsheet.delete_modal_title", {
            params: { name: userContext?.originalName ?? item.title },
          })}
          subtitle={t("medication_factsheet.delete_modal_message")}
          style={{ marginTop: 16, marginBottom: 20 }}
        />
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("medication_factsheet.delete_modal_cancel")}
            action="medication_factsheet.delete_modal_cancel"
            onPress={() => setDeleteConfirmVisible(false)}
            disabled={deleteMedication.isPending}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={t("medication_factsheet.delete_modal_confirm")}
            action="medication_factsheet.delete_modal_confirm"
            onPress={handleConfirmDelete}
            loading={deleteMedication.isPending}
            flex={1}
          />
        </DialogActions>
      </Dialog>
    </View>
  );
}

export default function MedicationFactsheetSheet({
  item,
  onClose,
  userContext,
  medicationId,
  onLogDosePress,
  onEditPress,
  fallbackName,
  marketContext,
}: Props) {
  if (!item && !fallbackName) return null;
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      {item ? (
        <MedicationDetailContent
          item={item}
          onClose={onClose}
          userContext={userContext}
          onEditPress={onEditPress}
          medicationId={medicationId}
          onLogDosePress={onLogDosePress}
          marketContext={marketContext}
        />
      ) : (
        <MedicationMinimalContent
          name={fallbackName ?? ""}
          userContext={userContext}
          medicationId={medicationId}
          onClose={onClose}
          onLogDosePress={onLogDosePress}
          onEditPress={onEditPress}
        />
      )}
    </Modal>
  );
}

function MedicationMinimalContent({
  name,
  userContext,
  medicationId,
  onClose,
  onLogDosePress,
  onEditPress,
}: {
  name: string;
  userContext?: MedicationUserContext;
  medicationId?: string;
  onClose: () => void;
  onLogDosePress?: () => void;
  onEditPress?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationMinimalContent");
  const { format: fmtDate } = useDateFormat();
  const updateMedication = useUpdateMedication();
  const deleteMedication = useDeleteMedication();
  const dosage = userContext?.dosage ?? null;
  const route = userContext?.route ? formatRoute(userContext.route, t) : null;
  const startDate = userContext?.startDate
    ? fmtDate(userContext.startDate)
    : null;
  const endDate = userContext?.endDate ? fmtDate(userContext.endDate) : null;
  const notes = userContext?.notes ?? null;
  const isStopped = isMedicationStopped(userContext?.notes);
  const isMutating = updateMedication.isPending || deleteMedication.isPending;
  const providerOwned = isProviderOwnedMedication(userContext?.source);
  const canStop = !!medicationId && !isStopped && !providerOwned;
  const canEdit = !!medicationId && !!onEditPress && !providerOwned;
  const canDelete = !!medicationId && !providerOwned;
  const showActions = canStop || canEdit;

  // Stop confirms inline and Delete in the shared delete confirmation:
  // Alert.alert isn't reliable on web.
  const [confirmMode, setConfirmMode] = useState<"view" | "stop">("view");
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [stopReason, setStopReason] = useState("");
  const [mutationError, setMutationError] = useState<string | null>(null);

  const openStopConfirm = useCallback(() => {
    setStopReason("");
    setMutationError(null);
    setConfirmMode("stop");
  }, []);

  const handleConfirmStop = useCallback(() => {
    if (!medicationId) {
      setMutationError(t("medication_factsheet.missing_med_id"));
      return;
    }
    setMutationError(null);
    const todayISO = getTodayString();
    const reason = stopReason.trim();
    updateMedication.mutate(
      {
        medicationId,
        endDate: todayISO,
        notes: prependStoppedMedicationNote(userContext?.notes, reason),
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          console.error("[MedicationMinimalFactsheet] Stop failed", err);
          setMutationError(
            err instanceof Error
              ? err.message
              : t("medication_factsheet.stop_med_error"),
          );
        },
      },
    );
  }, [
    medicationId,
    userContext?.notes,
    stopReason,
    updateMedication,
    onClose,
    t,
  ]);

  const handleConfirmDelete = useCallback(() => {
    if (!medicationId) {
      setMutationError(t("medication_factsheet.delete_med_error"));
      return;
    }
    setMutationError(null);
    deleteMedication.mutate(medicationId, {
      onSuccess: () => onClose(),
      onError: (err) => {
        console.error("[MedicationMinimalFactsheet] Delete failed", err);
        setMutationError(
          err instanceof Error
            ? err.message
            : t("medication_factsheet.delete_med_error"),
        );
      },
    });
  }, [medicationId, deleteMedication, onClose, t]);

  const closeDeleteConfirm = () => {
    if (deleteMedication.isPending) return;
    setMutationError(null);
    setDeleteConfirmVisible(false);
  };

  return (
    <>
      <Pressable
        onPress={() => {
          capturePress("dismiss_medication_sheet_backdrop");
          onClose();
        }}
        disabled={isMutating}
        style={{
          flex: 1,
          backgroundColor: semanticColors.overlay.scrim,
          justifyContent: "center",
          alignItems: "center",
          padding: 16,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            backgroundColor: EMBER.colors.surface,
            borderRadius: 20,
            width: "100%",
            maxWidth: 460,
            maxHeight: "90%",
            overflow: "hidden",
          }}
        >
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: 20,
              paddingVertical: 18,
            }}
            showsVerticalScrollIndicator={false}
          >
            {/* Close */}
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                marginBottom: 4,
              }}
            >
              <IconButton
                onPress={onClose}
                disabled={isMutating}
                hitSlop={8}
                size={28}
                surface="soft"
                backgroundColor={semanticColors.surface.muted}
                pressedBackgroundColor={semanticColors.surface.muted}
                pressedOpacity={1}
                accessibilityLabel="Close"
              >
                <X size={14} color={EMBER.colors.textLight} />
              </IconButton>
            </View>

            {/* Title */}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 24,
                fontWeight: "700",
                color: EMBER.colors.text,
                lineHeight: 30,
                marginBottom: 4,
                textAlign: "center",
              }}
            >
              {name}
            </Text>

            {/* No-factsheet advisory */}
            <ActionBanner
              variant="info"
              message={t("medication_factsheet.minimal_no_factsheet")}
              style={{ marginTop: 16, marginBottom: 8 }}
            />

            {/* Patient context */}
            <View
              style={{
                backgroundColor: withAlpha(semanticColors.accent.default, 0.04),
                borderRadius: 12,
                padding: 16,
                marginTop: 8,
                gap: 10,
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
              >
                <Pill size={14} color={EMBER.colors.warm} strokeWidth={2} />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "700",
                    color: EMBER.colors.warm,
                  }}
                >
                  {t("medication_factsheet.your_medication_fallback")}
                </Text>
              </View>
              {dosage ? (
                <InfoRow
                  label={t("medication_factsheet.row_dosage")}
                  value={dosage}
                />
              ) : null}
              {route ? (
                <InfoRow
                  label={t("medication_factsheet.row_route")}
                  value={route}
                />
              ) : null}
              {startDate ? (
                <InfoRow
                  label={t("medication_factsheet.row_start_date")}
                  value={startDate}
                />
              ) : null}
              {endDate ? (
                <InfoRow
                  label={t("medication_factsheet.row_end_date")}
                  value={endDate}
                />
              ) : null}
              {notes ? (
                <InfoRow
                  label={t("medication_factsheet.row_notes")}
                  value={notes}
                />
              ) : null}
              {!dosage && !route && !startDate && !endDate && !notes ? (
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 13, color: EMBER.colors.textLight }}
                >
                  {t("medication_factsheet.minimal_no_details")}
                </Text>
              ) : null}
            </View>

            {/* Actions */}
            {confirmMode !== "view" ? (
              <CardSurface style={{ marginTop: 24, padding: 14, gap: 12 }}>
                <View style={{ gap: 4 }}>
                  <CardTitleText>
                    {t("medication_factsheet.minimal_stop_title")}
                  </CardTitleText>
                  <SupportingSubtitleText>
                    {t("medication_factsheet.minimal_stop_help")}
                  </SupportingSubtitleText>
                </View>
                <TextField
                  value={stopReason}
                  onChangeText={setStopReason}
                  placeholder={t(
                    "medication_factsheet.minimal_stop_placeholder",
                  )}
                  notes
                />
                {mutationError && !deleteConfirmVisible ? (
                  <ActionBanner variant="important" message={mutationError} />
                ) : null}
                <View style={{ flexDirection: "row", gap: 8 }}>
                  <SecondaryButton
                    label={t("medication_factsheet.stop_form_cancel")}
                    action="medication_factsheet.stop_form_cancel"
                    onPress={() => {
                      setMutationError(null);
                      setConfirmMode("view");
                    }}
                    disabled={isMutating}
                    flex={1}
                  />
                  <PrimaryButton
                    label={t("medication_factsheet.stop")}
                    action="medication_factsheet.action_stop"
                    leading={
                      <CircleStop
                        size={16}
                        color={semanticColors.action.primary.foreground}
                        strokeWidth={1.8}
                      />
                    }
                    onPress={handleConfirmStop}
                    loading={updateMedication.isPending}
                    disabled={deleteMedication.isPending}
                    flex={1}
                  />
                </View>
                {canDelete ? (
                  <TextActionButton
                    label={t("medication_factsheet.delete_mistake_action")}
                    action="medication_factsheet.delete_mistake_action"
                    tone="destructive"
                    size="sm"
                    onPress={() => {
                      setMutationError(null);
                      setDeleteConfirmVisible(true);
                    }}
                    disabled={isMutating}
                    centered
                  />
                ) : null}
              </CardSurface>
            ) : showActions || providerOwned ? (
              <View style={{ marginTop: 24, gap: 8 }}>
                {providerOwned ? (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      backgroundColor: semanticColors.surface.muted,
                      borderRadius: 12,
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 14, fontWeight: "600" }}
                      >
                        {t("medication_factsheet.provider_owned_title")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}
                      >
                        {t("medication_factsheet.provider_owned_note")}
                      </Text>
                    </View>
                  </View>
                ) : null}
                {onLogDosePress && medicationId ? (
                  <MedicationActionRow
                    icon={ClipboardList}
                    title={t("medication_factsheet.action_log_dose")}
                    description={t("medication_factsheet.action_log_dose_help")}
                    onPress={onLogDosePress}
                    action="medication_factsheet.action_log_dose"
                    disabled={isMutating}
                  />
                ) : null}

                {canEdit ? (
                  <MedicationActionRow
                    icon={Pencil}
                    title={t("medication_factsheet.action_edit")}
                    description={t("medication_factsheet.action_edit_help")}
                    onPress={onEditPress}
                    action="medication_factsheet.action_edit"
                    disabled={isMutating}
                  />
                ) : null}

                {canStop ? (
                  <MedicationActionRow
                    icon={ShieldAlert}
                    title={
                      updateMedication.isPending
                        ? t("medication_factsheet.stop_form_submitting")
                        : t("medication_factsheet.action_stop")
                    }
                    description={t("medication_factsheet.action_stop_help")}
                    onPress={openStopConfirm}
                    action="medication_factsheet.action_stop"
                    disabled={isMutating}
                  />
                ) : null}
              </View>
            ) : null}
          </ScrollView>
        </Pressable>
      </Pressable>
      <Dialog
        visible={deleteConfirmVisible}
        onClose={closeDeleteConfirm}
        closeButton="minimal"
        testID="medication-minimal-delete-confirmation-dialog"
      >
        <DialogIcon source={deleteConfirmationIllustration} />
        <DialogHeader
          align="center"
          title={t("medication_factsheet.minimal_delete_title", {
            params: { name },
          })}
          subtitle={t("medication_factsheet.minimal_delete_help")}
          style={{ marginTop: 16, marginBottom: 20 }}
        />
        {mutationError ? (
          <ActionBanner
            variant="important"
            message={mutationError}
            style={{ marginBottom: 12 }}
          />
        ) : null}
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("medication_factsheet.stop_form_cancel")}
            action="medication_factsheet.stop_form_cancel"
            onPress={closeDeleteConfirm}
            disabled={deleteMedication.isPending}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={t("medication_factsheet.delete_modal_confirm")}
            action="medication_factsheet.action_delete"
            onPress={handleConfirmDelete}
            loading={deleteMedication.isPending}
            flex={1}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
